import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Group, Pagination, Text } from '@mantine/core';
import BestPriceButton from '@/components/common/BestPriceButton';
import BestPriceLink from '@/components/common/BestPriceLink';
import { prepaymentLabel } from '@/components/common/PrepaymentBadge';
import { offerQualityKey, useOffersQuality } from '@/hooks/useBestOffers';
import { bestOfferLink, bestPriceTone } from '@/lib/bestOffer';
import { formatOrgName, formatProperty, formatWeightTon } from '@/lib/format';
import type { MyBestOffer } from '@/types/bestPrice';
import classes from './home.module.css';

interface BestOffersListProps {
  readonly items: readonly MyBestOffer[];
}

const PAGE_SIZE = 6;

// «Лучшие предложения»: лучшие встречные цены по моим прайсам и предложениям (обе стороны). Цена — общим
// элементом BestPriceLink: крупное число, под ним выгода, кнопка «Продать» или «Купить» — единственная ссылка
// строки. Длинный список листается по PAGE_SIZE.
export default function BestOffersList({ items }: BestOffersListProps) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  // Главный параметр качества (протеин и т.п.) по каждому предложению — из словаря свойств.
  const qualityMap = useOffersQuality(items);
  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  // После обновления список мог сократиться: страница вне диапазона была бы пустой.
  const safePage = Math.min(page, totalPages);
  const pageItems = items.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <div>
      {pageItems.map((offer) => {
        const quality = qualityMap.get(offerQualityKey(offer.goodsType, offer.propertySetId));
        const action = offer.side === 'SELL' ? 'sell' : 'buy';
        const terms = [quality ? formatProperty(quality) : null, offer.weightTon != null ? formatWeightTon(offer.weightTon) : null]
          .filter(Boolean)
          .join(' · ');
        const place = [formatOrgName(offer.addressTitle), prepaymentLabel(offer.prepaymentType, t)].filter(Boolean).join(' · ');
        return (
          <div key={`${offer.side}-${offer.myOrderId}`} className={classes.row}>
            <Text fw={600}>{offer.goodsType ?? '—'}</Text>
            {terms ? (
              <Text size="sm" c="gray.7">
                {terms}
              </Text>
            ) : null}
            {place ? (
              <Text size="sm" c="gray.7">
                {place}
              </Text>
            ) : null}
            <Box mt={8}>
              {offer.bestPrice != null ? (
                <BestPriceLink
                  to={bestOfferLink(offer)}
                  price={offer.bestPrice}
                  action={action}
                  tone={bestPriceTone(offer.advantage)}
                  advantage={offer.advantage}
                />
              ) : (
                <Group justify="flex-end">
                  <BestPriceButton to={bestOfferLink(offer)} action={action} tone={bestPriceTone(offer.advantage)} />
                </Group>
              )}
            </Box>
          </div>
        );
      })}
      {totalPages > 1 ? (
        <Group justify="center" pt="md">
          <Pagination
            value={safePage}
            onChange={setPage}
            total={totalPages}
            size="md"
            color="brand"
            getControlProps={(control) => ({
              'aria-label': t(control === 'previous' ? 'app.home.bestOffers.prevPage' : 'app.home.bestOffers.nextPage'),
            })}
          />
        </Group>
      ) : null}
    </div>
  );
}
